import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getOrder, submitOrderRating, cancelOrder, requestRevokeRA, downloadAcknowledgementSlip, generateShareLink, requestOrderPaymentMethod } from "../../api/client";
import { trackOrder } from "../../api/socket";
import { PACKAGE_TYPE_LABELS } from "../../utils/packageLabels";
import BottomNav from "../../components/BottomNav";
import UserPageHeader from "../../components/UserPageHeader";
import UserTrackingMap from "../../components/UserTrackingMap";
import OrderChatModal from "../../components/OrderChatModal";
import ScreenPopupModal from "../../components/ScreenPopupModal";

const CANCELLABLE_STATUSES = ["scheduled", "requested", "accepted"];
const USER_CANCEL_REASONS = [
  { value: "delay", label: "Taking too long" },
  { value: "change_of_mind", label: "Changed my mind" },
  { value: "others", label: "Other reason" },
];

const STEPS = [
  { key: "requested",       label: "Finding a Dispatcher"  },
  { key: "accepted",        label: "Dispatcher Assigned"   },
  { key: "arrived_at_pickup", label: "Dispatcher at Pickup" },
  { key: "picked_up",       label: "Package Picked Up"     },
  { key: "in_transit",      label: "In Transit"            },
  { key: "delivered",       label: "Delivered"             }
];

const STATUS_ALERT_MESSAGE = {
  accepted:          "✅ A dispatcher has been assigned to your delivery!",
  arrived_at_pickup: "📍 Your dispatcher has arrived at the pickup location.",
  picked_up:         "📦 Your package has been picked up!",
  in_transit:        "🚀 Your delivery is now on its way!",
  delivered:         "🎉 Your package has been delivered!",
  completed:         "✅ Your delivery is complete. Thank you!",
  cancelled:         "❌ Your order has been cancelled.",
  failed:            "⚠️ Your delivery could not be completed."
};

const STATUS_BADGE_CLASS = {
  requested:        "warning",
  accepted:         "info",
  arrived_at_pickup:"info",
  picked_up:        "info",
  in_transit:       "info",
  delivered:        "",
  completed:        "",
  cancelled:        "danger",
  failed:           "danger"
};

export default function Track() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [riderLocation, setRiderLocation] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancelReason, setCancelReason] = useState("delay");
  const [cancelNote, setCancelNote] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeMessage, setRevokeMessage] = useState("");
  const [slipBusy, setSlipBusy] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [shareLinkBusy, setShareLinkBusy] = useState(false);
  const [shareLinkError, setShareLinkError] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [statusAlert, setStatusAlert] = useState("");
  const [switchBusy, setSwitchBusy] = useState(false);
  const [switchError, setSwitchError] = useState("");
  const [switchMessage, setSwitchMessage] = useState("");

  const handleRequestSwitchPayment = async (method) => {
    setSwitchBusy(true);
    setSwitchError("");
    setSwitchMessage("");
    try {
      const { data } = await requestOrderPaymentMethod(orderId, method);
      setOrder(data.order);
      if (data.pendingConfirmation) {
        setSwitchMessage("⏳ Switch requested — awaiting dispatcher confirmation.");
      } else {
        setSwitchMessage("✅ Payment method updated successfully.");
      }
    } catch (err) {
      setSwitchError(err.response?.data?.error || "Failed to switch payment method.");
    } finally {
      setSwitchBusy(false);
    }
  };

  const shareUrl = order?.publicShareToken ? `${window.location.origin}/share/${order.publicShareToken}` : "";

  // The token is never generated automatically — only when the sender
  // explicitly asks for it here. Once created it's saved on the order
  // permanently (see orderController.generateShareLink), so calling this
  // again just fetches the same link back rather than rotating it.
  const handleGenerateShareLink = async () => {
    setShareLinkBusy(true);
    setShareLinkError("");
    try {
      const { data } = await generateShareLink(order._id);
      setOrder(o => ({ ...o, publicShareToken: data.publicShareToken }));
    } catch (err) {
      setShareLinkError(err.response?.data?.error || "Could not create the share link.");
    } finally {
      setShareLinkBusy(false);
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard?.writeText(shareUrl);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } catch { /* clipboard access denied — the input is still selectable/copyable manually */ }
  };

  const handleDownloadSlip = async () => {
    setSlipBusy(true);
    setError("");
    try {
      const { data } = await downloadAcknowledgementSlip(order._id);
      const blobUrl = URL.createObjectURL(data);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      setError("Could not download the acknowledgement slip.");
    } finally {
      setSlipBusy(false);
    }
  };

  useEffect(() => {
    async function load() {
      try {
        const { data } = await getOrder(orderId);
        setOrder(data);
      } catch (err) {
        setError(err.response?.data?.error || "Could not load order");
      }
    }
    load();

    const unsubscribe = trackOrder(orderId, {
      onStatus: ({ status, rider }) => {
        setOrder((prev) => (prev ? { ...prev, status, ...(rider ? { rider } : {}) } : prev));
        load();
        // Show a live status-change banner at the top of the page
        const alertMsg = STATUS_ALERT_MESSAGE[status];
        if (alertMsg) {
          setStatusAlert(alertMsg);
          setTimeout(() => setStatusAlert(""), 6000);
        }
        if (status === "accepted") {
          setChatOpen(true);
        }
      },
      onIssue: ({ issue }) => setOrder((prev) => (prev ? { ...prev, issue } : prev)),
      onLocation: (loc) => setRiderLocation(loc)
    });

    return unsubscribe;
  }, [orderId]);

  const hasRider = !!(order?.rider || ["accepted", "arrived_at_pickup", "picked_up", "in_transit"].includes(order?.status));
  const riderName = typeof order?.rider === "object" && order?.rider?.name ? order.rider.name : "Assigned Dispatcher";

  if (error) return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/orders" backLabel="My Orders" />
        <div className="page">
          <p className="error">{error}</p>
          <Link to="/orders"><button className="secondary" style={{ marginTop: "12px" }}>← Back to My Orders</button></Link>
        </div>
      </div>
    </div>
  );

  if (!order) return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/orders" backLabel="My Orders" />
        <div className="page">
          <div className="empty-state">
            <span className="empty-state-icon">📡</span>
            <p className="empty-state-text waiting-dots">Connecting to order tracking</p>
          </div>
        </div>
      </div>
    </div>
  );

  const currentStepIndex = STEPS.findIndex((s) => s.key === order.status);

  return (
    <div className="user-app">
      <BottomNav />
      <div className="user-app-main">
        <UserPageHeader backTo="/orders" backLabel="My Orders" />

        <div className="page">
        <div className="brand-header">
          <span className="brand-logo">📍</span>
          <span className="brand-name">Live Tracking</span>
          <p className="brand-tagline">Order #{order._id.slice(-6).toUpperCase()}</p>
        </div>

        {/* Live status-change notification banner */}
        {statusAlert && (
          <div
            role="alert"
            style={{
              background: "var(--text-accent)",
              color: "#fff",
              borderRadius: "10px",
              padding: "12px 16px",
              marginBottom: "16px",
              fontWeight: 600,
              fontSize: "14px",
              textAlign: "center",
              animation: "slideDown 0.3s ease",
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)"
            }}
          >
            {statusAlert}
          </div>
        )}

        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <span className={`status-badge ${STATUS_BADGE_CLASS[order.status] || ""}`}>
            {order.status.replace(/_/g, " ").toUpperCase()}
          </span>
        </div>

        {order.status === "completed" && (
          <div className="quote-box" style={{ marginBottom: "16px", textAlign: "center", background: "var(--success-bg)", borderColor: "var(--success-border)" }}>
            <p style={{ fontSize: "32px", marginBottom: "4px" }}>🎉</p>
            <p style={{ color: "var(--success)", fontWeight: 700, fontSize: "16px" }}>Delivery Completed!</p>
            {order.paymentMethod === "cash" ? (
              <p className="hint" style={{ marginTop: "6px" }}>
                Cash payment of <strong style={{ color: "var(--text-primary)" }}>₦{order.priceSnapshot.total.toLocaleString()}</strong> was collected by your dispatcher on delivery.
              </p>
            ) : (
              <p className="hint" style={{ marginTop: "6px" }}>
                You paid <strong style={{ color: "var(--text-primary)" }}>₦{order.priceSnapshot.total.toLocaleString()}</strong> — no further payment is needed for this delivery.
              </p>
            )}
          </div>
        )}

        {order.status === "cancelled" && order.cancellation?.by && (
          <div className="quote-box" style={{ marginBottom: "16px", textAlign: "center" }}>
            <p style={{ color: "var(--danger-hover)", fontWeight: 600 }}>
              Cancelled by {order.cancellation.by} — {order.cancellation.reason?.replace(/_/g, " ")}
            </p>
            {order.cancellation.note && <p className="hint">{order.cancellation.note}</p>}
          </div>
        )}

        {order.issue?.status === "open" && (
          <div className="quote-box" style={{ marginBottom: "16px", textAlign: "center", background: "var(--warning-bg)", borderColor: "var(--warning-border)" }}>
            <p style={{ color: "var(--warning-hover)", fontWeight: 600 }}>
              🚨 Your dispatcher flagged an issue with this delivery — our support team will call you shortly.
            </p>
          </div>
        )}

        {/* Visual Stepper */}
        {currentStepIndex !== -1 && (
          <div className="timeline">
            {STEPS.map((step, idx) => {
              let stateClass = "";
              if (idx < currentStepIndex) stateClass = "completed";
              else if (idx === currentStepIndex) stateClass = "active";

              return (
                <div key={step.key} className={`timeline-step ${stateClass}`}>
                  <div className="timeline-dot" />
                  <div className="timeline-label">{step.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Live Goboot Tracking Map */}
        <UserTrackingMap order={order} riderLocation={riderLocation} />

        {/* Route Info */}
        <div className="delivery-card">
          <div className="info-row">
            <span className="label">Pickup Address</span>
            <span className="value">{order.pickup.address}</span>
          </div>
          {order.pickupContact?.name && (
            <div className="info-row">
              <span className="label">👤 Pickup Contact</span>
              <span className="value">{order.pickupContact.name} ({order.pickupContact.phone})</span>
            </div>
          )}
          {order.stops?.map((s, i) => (
            <div key={i} className="info-row">
              <span className="label">Stop {i + 1}</span>
              <span className="value">{s.address}</span>
            </div>
          ))}
          <div className="info-row">
            <span className="label">Drop-off Address</span>
            <span className="value">{order.dropoff.address}</span>
          </div>
          {order.dropoffContact?.name && (
            <div className="info-row">
              <span className="label">🏁 Drop-off Contact</span>
              <span className="value">{order.dropoffContact.name} ({order.dropoffContact.phone})</span>
            </div>
          )}
          <div className="info-row">
            <span className="label">Package Size</span>
            <span className="value">{PACKAGE_TYPE_LABELS[order.packageType] || order.packageType}</span>
          </div>
          {order.vehicleType === "truck" ? (
            (order.packageDescription || order.cargoWeight) && (
              <div className="info-row">
                <span className="label">Cargo</span>
                <span className="value">{order.packageDescription || "-"}{order.cargoWeight ? ` · ${order.cargoWeight}` : ""}</span>
              </div>
            )
          ) : (
            <>
              {order.packageDescription && (
                <div className="info-row">
                  <span className="label">Package Description</span>
                  <span className="value">{order.packageDescription}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Rider Info */}
        {hasRider && (
          <div className="quote-box">
            <h3>Dispatcher Information</h3>
            <div className="info-row">
              <span className="label">Name</span>
              <span className="value">{riderName}</span>
            </div>
            {typeof order.rider === "object" && order.rider?.phone && (
              <div className="info-row">
                <span className="label">Phone</span>
                <span className="value">{order.rider.phone}</span>
              </div>
            )}
            {typeof order.rider === "object" && (
              <div className="info-row">
                <span className="label">Rating</span>
                <span className="value">⭐ {order.rider.rating ? order.rider.rating.toFixed(1) : "New"}</span>
              </div>
            )}
            {riderLocation && (
              <div className="info-row">
                <span className="label">Live Coordinates</span>
                <span className="value">{riderLocation.lat.toFixed(4)}, {riderLocation.lng.toFixed(4)}</span>
              </div>
            )}
            <button
              type="button"
              className={chatOpen ? "primary" : "secondary"}
              style={{
                marginTop: "12px",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                fontWeight: 600,
                cursor: "pointer"
              }}
              onClick={() => setChatOpen((prev) => !prev)}
            >
              <span>💬</span> {chatOpen ? "Delivery Chat Open (Click to Close)" : `Chat with ${riderName}`}
            </button>
          </div>
        )}

        {/* Payment summary */}
        <div className="delivery-card" style={{ marginTop: "16px" }}>
          <div className="info-row">
            <span className="label">{order.paymentMethod === "receiver_pay" ? "Delivery Fee" : "Amount Paid"}</span>
            <span className="value">
              ₦{order.priceSnapshot.total} ({
                order.paymentMethod === "cash" ? "Cash on Delivery" :
                order.paymentMethod === "bank_transfer" ? "Pay on Delivery (Bank Transfer)" :
                order.paymentMethod === "receiver_pay" ? (order.paymentStatus === "paid" ? "Paid by receiver" : "Receiver pays via link") :
                order.paymentMethod === "wallet" ? "Wallet" :
                order.paymentMethod === "flutterwave" ? "Flutterwave" :
                order.paymentMethod === "b2b_invoice" ? "Business Invoice" :
                "Paystack"
              })
            </span>
          </div>

          {/* Switch payment option for POD orders in progress */}
          {!["completed", "cancelled", "failed"].includes(order.status) && ["cash", "bank_transfer"].includes(order.paymentMethod) && (
            <div style={{ marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--border)" }}>
              {order.paymentMethodChangeRequest?.status === "pending" ? (
                <div style={{ padding: "8px 10px", background: "var(--warning-bg)", borderRadius: "6px", color: "var(--warning-hover)", fontSize: "13px", fontWeight: "600" }}>
                  ⏳ Waiting for dispatcher to approve switch to {order.paymentMethodChangeRequest.requestedMethod === "cash" ? "Cash on Delivery" : "Bank Transfer"}...
                </div>
              ) : (
                <div>
                  {switchError && <p className="error" style={{ marginBottom: "8px" }}>{switchError}</p>}
                  {switchMessage && <p className="hint" style={{ color: "var(--success)", marginBottom: "8px" }}>{switchMessage}</p>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                      Need to switch payment method?
                    </span>
                    {order.paymentMethod === "bank_transfer" ? (
                      <button
                        type="button"
                        className="secondary"
                        style={{ padding: "6px 12px", fontSize: "12px", marginTop: 0 }}
                        disabled={switchBusy}
                        onClick={() => handleRequestSwitchPayment("cash")}
                      >
                        {switchBusy ? "Requesting..." : "Switch to Cash (COD)"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="secondary"
                        style={{ padding: "6px 12px", fontSize: "12px", marginTop: 0 }}
                        disabled={switchBusy}
                        onClick={() => handleRequestSwitchPayment("bank_transfer")}
                      >
                        {switchBusy ? "Switching..." : "Switch to Bank Transfer"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Public share link — anyone with it can see order details/tracking
            with no account; if payment was deferred to the receiver, they
            can also pay from it. Not generated automatically — only when
            you ask for it below — but once created it's saved permanently. */}
        <div className="quote-box" style={{ marginTop: "16px" }}>
          <h3>🔗 Share With Your Receiver</h3>
          <p className="hint" style={{ marginBottom: "8px" }}>
            {order.paymentMethod === "receiver_pay" && order.paymentStatus !== "paid"
              ? "Create a link your receiver can open with no account, to see live delivery details and pay the delivery fee."
              : "Create a link your receiver can open with no account, to see live delivery details and tracking."}
          </p>
          {shareLinkError && <p className="error">{shareLinkError}</p>}
          {shareUrl ? (
            <>
              <input
                type="text"
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                style={{ fontSize: "13px", marginBottom: "8px" }}
              />
              <button type="button" onClick={handleCopyShareLink}>
                {shareLinkCopied ? "✅ Copied!" : "📋 Copy Link"}
              </button>
            </>
          ) : (
            <button type="button" onClick={handleGenerateShareLink} disabled={shareLinkBusy}>
              {shareLinkBusy ? "Creating link..." : "🔗 Create Share Link"}
            </button>
          )}
        </div>

        {/* Delivery Token / Receiver Acknowledgement */}
        {order.deliveryToken && !["cancelled", "failed"].includes(order.status) && (
          <div className="quote-box" style={{ marginTop: "16px" }}>
            <h3>Delivery Verification</h3>
            <p className="hint" style={{ marginBottom: "8px" }}>
              Share this code with your rider or receiver — {order.receiverAcknowledgement?.enabled ? "your chosen verification method applies" : "used to confirm handoff"}.
            </p>
            <div style={{ fontSize: "26px", fontWeight: 800, letterSpacing: "4px", textAlign: "center", padding: "8px 0" }}>
              {order.deliveryToken.code}
            </div>
            {order.receiverAcknowledgement?.enabled && (
              <>
                <div className="info-row">
                  <span className="label">RA Method</span>
                  <span className="value">{order.receiverAcknowledgement.method.replace(/_/g, " ")}</span>
                </div>
                <div className="info-row">
                  <span className="label">RA Fee</span>
                  <span className="value">₦{order.receiverAcknowledgement.fee}</span>
                </div>
                {order.receiverAcknowledgement.revokeStatus === "none" && !["delivered", "completed"].includes(order.status) && (
                  <button
                    className="secondary"
                    style={{ marginTop: "8px" }}
                    disabled={revokeBusy}
                    onClick={async () => {
                      setRevokeBusy(true);
                      setRevokeMessage("");
                      try {
                        await requestRevokeRA(order._id);
                        setRevokeMessage("Revoke request sent — your dispatcher must approve it in-app.");
                      } catch (err) {
                        setRevokeMessage(err.response?.data?.error || "Could not request revoke.");
                      } finally {
                        setRevokeBusy(false);
                      }
                    }}
                  >
                    Request to Revoke Receiver Acknowledgement
                  </button>
                )}
                {order.receiverAcknowledgement.revokeStatus === "requested" && (
                  <p className="hint" style={{ color: "var(--warning)" }}>⏳ Revoke requested — awaiting dispatcher approval.</p>
                )}
                {order.receiverAcknowledgement.revokeStatus === "approved" && (
                  <p className="hint" style={{ color: "var(--text-accent)" }}>✓ Receiver Acknowledgement revoked.</p>
                )}
                {revokeMessage && <p className="hint">{revokeMessage}</p>}
              </>
            )}
          </div>
        )}

        {/* Acknowledgement Slip Download */}
        {["delivered", "completed"].includes(order.status) && (
          <button className="secondary" style={{ width: "100%", marginTop: "12px" }} disabled={slipBusy} onClick={handleDownloadSlip}>
            {slipBusy ? "Preparing..." : "📄 Download Acknowledgement Slip (PDF)"}
          </button>
        )}

        {/* Cancel Order */}
        {CANCELLABLE_STATUSES.includes(order.status) && (
          <div className="quote-box" style={{ marginTop: "16px" }}>
            {!showCancelForm ? (
              <button className="secondary" style={{ color: "var(--danger-hover)" }} onClick={() => setShowCancelForm(true)}>
                ✕ Cancel This Delivery
              </button>
            ) : (
              <>
                <h3>Cancel Delivery</h3>
                <div className="form-field">
                  <label>Reason</label>
                  <select value={cancelReason} onChange={e => setCancelReason(e.target.value)}>
                    {USER_CANCEL_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <textarea
                  placeholder="Additional details (optional)"
                  value={cancelNote}
                  onChange={e => setCancelNote(e.target.value)}
                  rows={2}
                  style={{ marginBottom: "8px", resize: "vertical" }}
                />
                {error && <p className="error">{error}</p>}
                <div className="row">
                  <button className="secondary" onClick={() => setShowCancelForm(false)} disabled={cancelBusy}>Never Mind</button>
                  <button
                    style={{ background: "var(--danger)" }}
                    disabled={cancelBusy}
                    onClick={async () => {
                      setCancelBusy(true);
                      setError("");
                      try {
                        const { data } = await cancelOrder(order._id, cancelReason, cancelNote);
                        setOrder(data);
                        setShowCancelForm(false);
                      } catch (err) {
                        setError(err.response?.data?.error || "Could not cancel order.");
                      } finally {
                        setCancelBusy(false);
                      }
                    }}
                  >
                    {cancelBusy ? "Cancelling..." : "Confirm Cancellation"}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Star Rating — shown for completed orders not yet rated */}
        {["delivered", "completed"].includes(order.status) && !order.rating && !ratingSubmitted && (
          <div className="quote-box" style={{ marginTop: "16px", textAlign: "center" }}>
            <h3>Rate Your Delivery</h3>
            <p style={{ color: "var(--text-muted)", marginBottom: "12px" }}>How was your experience?</p>
            <div style={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "16px" }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  style={{
                    fontSize: "28px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    opacity: rating >= star ? 1 : 0.35,
                    padding: "4px",
                    transition: "opacity 0.15s"
                  }}
                >⭐</button>
              ))}
            </div>
            <textarea
              placeholder="Any comments or observations? (optional)"
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value)}
              rows={3}
              style={{ marginBottom: "12px", resize: "vertical", textAlign: "left" }}
            />
            <button
              disabled={rating === 0 || ratingBusy}
              onClick={async () => {
                setRatingBusy(true);
                try {
                  await submitOrderRating(order._id, rating, ratingComment);
                  setRatingSubmitted(true);
                } catch {}
                finally { setRatingBusy(false); }
              }}
            >
              {ratingBusy ? "Submitting..." : "Submit Rating"}
            </button>
          </div>
        )}
        {ratingSubmitted && (
          <div className="quote-box" style={{ marginTop: "16px", textAlign: "center" }}>
            <span style={{ fontSize: "32px" }}>🎉</span>
            <p style={{ fontWeight: 700, color: "var(--text-accent)" }}>Thank you for your rating!</p>
          </div>
        )}

        {/* Real-time Delivery Chat (Active once rider accepts) */}
        {hasRider && (
          <OrderChatModal
            order={order}
            currentRole="user"
            counterpartyName={riderName}
            isOpen={chatOpen}
            onOpen={() => setChatOpen(true)}
            onClose={() => setChatOpen(false)}
          />
        )}

        {/* View All Orders */}
        <Link to="/orders">
          <button className="secondary" style={{ width: "100%", marginTop: "16px" }}>← View All My Orders</button>
        </Link>
      </div>
    </div>
  </div>
);
}
